import { useState, useEffect } from 'react';
import { client, billingClient } from '../../sdkAdapter';
import Swal from 'sweetalert2';
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import Button from '../ui/button/Button';
import BillingModal from '../modals/BillingModal';
import CreditModal from '../modals/CreditModal';

export default function BillingTable() {
    const [billingDetails, setBillingDetails] = useState<any[]>([]);
    const [credits, setCredits] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    // Modals
    const [billingModalOpen, setBillingModalOpen] = useState(false);
    const [selectedBilling, setSelectedBilling] = useState<any>(null);

    const [creditModalOpen, setCreditModalOpen] = useState(false);
    const [selectedCredit, setSelectedCredit] = useState<any>(null);

    const fetchData = async () => {
        setLoading(true);


        // Fetch Billing Details
        try {
            const billingRes = await billingClient.getBillingDetails() as any;
            const bData = billingRes.data?.data?.data || billingRes.data?.data || billingRes.data || [];
            setBillingDetails(Array.isArray(bData) ? bData : []);
        } catch (err) {
            console.error('Failed to fetch billing details:', err);
            // Don't set global error yet, we want to try credits too
        }

        // Fetch Credits
        try {
            // Tentative: the SDK says credits takes an optional request params, 
            // but the backend action says IndexCreditAction($id, IndexCreditRequest $request).
            // This might cause a 404/500 if id is missing in Route (Routes say /credits and /credit/{id})
            let creditsRes;
            try {
                creditsRes = await client.getApiBillingCredits() as any;
            } catch (creditsErr) {
                console.warn('client.getApiBillingCredits failed, trying fallback to /credit/0');
                // Fallback attempt: some APIs use 0 or 'all' or similar if index is misbehaving
                creditsRes = await client.getApiBillingCreditId({ id: '0' }) as any;
            }

            const cData = creditsRes.data?.data?.data || creditsRes.data?.data || creditsRes.data || [];
            setCredits(Array.isArray(cData) ? cData : []);
        } catch (err) {
            console.error('Failed to fetch credits:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleDeleteBilling = async (id: number) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "Are you sure you want to delete this billing detail?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (!result.isConfirmed) return;
        try {
            await client.postApiBillingBillingDetailsDelete({
                postApiBillingBillingDetailsDeleteRequest: { id }
            });
            fetchData();
        } catch (err: any) {
            alert('Failed to delete: ' + (err?.message || 'Unknown error'));
        }
    };

    const handleDeleteCredit = async (id: number) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "Are you sure you want to delete this credit?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (!result.isConfirmed) return;
        try {
            await client.postApiBillingCreditDelete({
                postApiBillingCreditDeleteRequest: { id }
            });
            fetchData();
        } catch (err: any) {
            alert('Failed to delete: ' + (err?.message || 'Unknown error'));
        }
    };

    return (
        <div className="space-y-6">
            {/* Billing Details Section */}
            <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden">
                <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Billing Details</h3>
                    <Button onClick={() => { setSelectedBilling(null); setBillingModalOpen(true); }}>
                        Add Billing Detail
                    </Button>
                </div>
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableCell isHeader className="px-5 py-3 text-start">Name</TableCell>
                                <TableCell isHeader className="px-5 py-3 text-start">City</TableCell>
                                <TableCell isHeader className="px-5 py-3 text-start">Address</TableCell>
                                <TableCell isHeader className="px-5 py-3 text-end">Actions</TableCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && billingDetails.length === 0 ? (
                                <TableRow><TableCell colSpan={4} className="px-5 py-4 text-center">Loading...</TableCell></TableRow>
                            ) : billingDetails.length === 0 ? (
                                <TableRow><TableCell colSpan={4} className="px-5 py-4 text-center">No billing details found.</TableCell></TableRow>
                            ) : (
                                billingDetails.map(item => (
                                    <TableRow key={item.id}>
                                        <TableCell className="px-5 py-4 font-medium">{item.name}</TableCell>
                                        <TableCell className="px-5 py-4">{item.city}</TableCell>
                                        <TableCell className="px-5 py-4 text-gray-500">{item.address_1}</TableCell>
                                        <TableCell className="px-5 py-4 text-end">
                                            <div className="flex justify-end gap-2">
                                                <button onClick={() => { setSelectedBilling(item); setBillingModalOpen(true); }} className="text-blue-600 hover:underline"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                                </button>
                                                <button onClick={() => handleDeleteBilling(item.id)} className="text-red-600 hover:underline"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                                </button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {/* Credits Section */}
            <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden">
                <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Credits</h3>
                    <Button onClick={() => { setSelectedCredit(null); setCreditModalOpen(true); }}>
                        Add Credit
                    </Button>
                </div>
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableCell isHeader className="px-5 py-3 text-start">Amount</TableCell>
                                <TableCell isHeader className="px-5 py-3 text-start">Expiration</TableCell>
                                <TableCell isHeader className="px-5 py-3 text-start">Active</TableCell>
                                <TableCell isHeader className="px-5 py-3 text-end">Actions</TableCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && credits.length === 0 ? (
                                <TableRow><TableCell colSpan={4} className="px-5 py-4 text-center">Loading...</TableCell></TableRow>
                            ) : credits.length === 0 ? (
                                <TableRow><TableCell colSpan={4} className="px-5 py-4 text-center">No credits found.</TableCell></TableRow>
                            ) : (
                                credits.map(item => (
                                    <TableRow key={item.id}>
                                        <TableCell className="px-5 py-4 font-bold text-green-600">${Number(item.amount).toFixed(2)}</TableCell>
                                        <TableCell className="px-5 py-4">{item.expiration_data || 'N/A'}</TableCell>
                                        <TableCell className="px-5 py-4">
                                            {item.active ?
                                                <span className="text-green-600 font-medium text-xs bg-green-100 px-2 py-1 rounded">Active</span> :
                                                <span className="text-gray-500 font-medium text-xs bg-gray-100 px-2 py-1 rounded">Inactive</span>
                                            }
                                        </TableCell>
                                        <TableCell className="px-5 py-4 text-end">
                                            <div className="flex justify-end gap-2">
                                                <button onClick={() => { setSelectedCredit(item); setCreditModalOpen(true); }} className="text-blue-600 hover:underline"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                                </button>
                                                <button onClick={() => handleDeleteCredit(item.id)} className="text-red-600 hover:underline"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                                </button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>

            <BillingModal
                isOpen={billingModalOpen}
                onClose={() => setBillingModalOpen(false)}
                onSave={fetchData}
                billing={selectedBilling}
            />

            <CreditModal
                isOpen={creditModalOpen}
                onClose={() => setCreditModalOpen(false)}
                onSave={fetchData}
                credit={selectedCredit}
            />
        </div>
    );
}
